import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiError, fmtIDR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";
import { printAny, receiptHTML, billHTML, testHTML, connectBluetooth, bluetoothConnected } from "@/lib/print";
import { isOnline, enqueue, queueCount, cacheBranch, getCachedBranch, cacheProducts, getCachedProducts, syncQueue } from "@/lib/offline";
import { AttendanceDialog } from "@/components/AttendanceDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, Minus, Ticket, AlertTriangle, Printer, Wallet, X, ShoppingCart, CheckCircle2, LayoutGrid, Settings, LockKeyhole, Bluetooth, DoorOpen, DoorClosed, ListOrdered, Save, FileText, Ban, WifiOff, RefreshCw, Monitor, ChefHat, UserRound, Camera, Eye, Loader2 } from "lucide-react";

const PAY = [["cash", "Tunai"], ["qris", "QRIS"], ["debit", "Debit"], ["credit", "Kredit"]];
const QUICK_CASH = [50000, 100000, 150000, 200000];

export default function KasirPOS() {
  const { user } = useAuth();
  const [branch, setBranch] = useState(null);
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState("Semua");
  const [cart, setCart] = useState([]);
  const [voucher, setVoucher] = useState(null);
  const [voucherCode, setVoucherCode] = useState("");
  const [showPay, setShowPay] = useState(false);
  const [member, setMember] = useState(null);
  const [showMember, setShowMember] = useState(false);
  const [memberInput, setMemberInput] = useState("");
  const [showLow, setShowLow] = useState(false);
  const [showAttendance, setShowAttendance] = useState(false);
  const [openPwd, setOpenPwd] = useState("");
  const [showVoucher, setShowVoucher] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [pay, setPay] = useState({ method: "cash", cash: 0, trace: "" });
  const [receipt, setReceipt] = useState(null);
  const [mode, setMode] = useState("self");
  const [table, setTable] = useState("");
  const [shift, setShift] = useState(null);        // null=loading, {} = closed, {..}=open
  const [openCash, setOpenCash] = useState(0);
  const [recap, setRecap] = useState(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const [paper, setPaper] = useState(localStorage.getItem("jjl_paper") || "80");
  const [autoCut, setAutoCut] = useState(localStorage.getItem("jjl_autocut") !== "0");
  const [btName, setBtName] = useState(null);
  const [billId, setBillId] = useState(null);
  const [addMode, setAddMode] = useState(false);
  const [addBaseItems, setAddBaseItems] = useState([]);
  const [sendingAdd, setSendingAdd] = useState(false);
  const [bills, setBills] = useState([]);
  const [showBills, setShowBills] = useState(false);
  const [txns, setTxns] = useState([]);
  const [showTxns, setShowTxns] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelPwd, setCancelPwd] = useState("");
  const [online, setOnline] = useState(isOnline());
  const [pending, setPending] = useState(queueCount());
  const [specials, setSpecials] = useState([]);
  const [svcCharge, setSvcCharge] = useState(user?.service_charge ?? 0);
  const [svcInput, setSvcInput] = useState(String(user?.service_charge ?? 0));
  const [svcMgrPwd, setSvcMgrPwd] = useState("");
  const [savingSvc, setSavingSvc] = useState(false);

  const load = async () => {
    try {
      const [br, pr, sh] = await Promise.all([api.get("/branches"), api.get("/products"), api.get("/shift/current")]);
      const b = br.data.find((x) => x.id === user.branch_id) || br.data[0];
      setBranch(b); cacheBranch(b);
      const prods = pr.data.filter((p) => !b || p.branch_id === b.id);
      setProducts(prods); cacheProducts(b?.id, prods);
      setShift(sh.data && sh.data.id ? sh.data : {});
      try { const sp = await api.get("/special-discounts/active", { params: { branch_id: b?.id || "" } }); setSpecials(sp.data); } catch (e2) {}
    } catch (e) {
      const cb = getCachedBranch();
      if (cb) {
        setBranch(cb);
        setProducts(getCachedProducts(cb.id));
        setShift((s) => (s && s.id ? s : { id: "offline", offline: true }));
        toast("Mode Offline · memakai data tersimpan", { icon: "📴" });
      } else { toast.error(apiError(e)); }
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const goOnline = async () => {
      setOnline(true);
      const { synced } = await syncQueue();
      setPending(queueCount());
      if (synced > 0) { toast.success(`${synced} transaksi offline tersinkron`); load(); }
    };
    const goOffline = () => { setOnline(false); toast("Koneksi terputus · mode Offline aktif", { icon: "📴" }); };
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  const manualSync = async () => {
    if (!isOnline()) { toast.error("Masih offline · coba lagi saat online"); return; }
    const { synced } = await syncQueue();
    setPending(queueCount());
    if (synced > 0) { toast.success(`${synced} transaksi tersinkron`); load(); }
    else toast("Tidak ada transaksi tertunda");
  };

  // Customer Facing Display sync (BroadcastChannel + localStorage)
  useEffect(() => {
    const state = {
      branch_name: branch?.name || "DA LE TANG HuoGuo",
      running_text: branch?.running_text || "",
      items: cart.map((i) => ({ name: i.name, qty: i.qty, price: i.price, orig_price: i.orig_price, on_discount: i.on_discount })),
      subtotal, special_discount: specialDiscount, discount, service, service_rate: svcRate, tax, tax_rate: taxRate, total, mode, table,
      member: member ? { name: member.name, points: member.points } : null,
      voucher_code: voucher?.code || "",
    };
    try { localStorage.setItem("jjl_cfd_state", JSON.stringify(state)); } catch (e) {}
    try { const bc = new BroadcastChannel("jjl-cfd"); bc.postMessage(state); bc.close(); } catch (e) {}
  });

  // Barcode scanner (acts as keyboard: rapid keys ending with Enter)
  useEffect(() => {
    let buf = "", last = 0;
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      const now = Date.now();
      if (now - last > 120) buf = "";
      last = now;
      if (e.key === "Enter") {
        if (buf.length >= 3) {
          const p = products.find((x) => String(x.barcode) === buf);
          if (p) { add(p); toast.success(`Scan: ${p.name}`); } else toast.error(`Barcode ${buf} tidak ditemukan`);
        }
        buf = "";
      } else if (/^[0-9A-Za-z]$/.test(e.key)) { buf += e.key; }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [products]);

  const cats = useMemo(() => ["Semua", ...Array.from(new Set(products.map((p) => p.category)))], [products]);
  const shown = cat === "Semua" ? products : products.filter((p) => p.category === cat);
  const priceOf = (p) => (p.on_discount ? p.effective_price : p.price);

  const add = (p) => {
    const price = priceOf(p);
    setCart((c) => {
      const ex = c.find((i) => i.product_id === p.id);
      if (ex) return c.map((i) => i.product_id === p.id ? { ...i, qty: i.qty + 1 } : i);
      return [...c, { product_id: p.id, name: p.name, price, orig_price: p.price, on_discount: !!p.on_discount, qty: 1, note: "", category: p.category }];
    });
  };
  const changeQty = (id, d) => setCart((c) => c.map((i) => i.product_id === id ? { ...i, qty: Math.max(0, i.qty + d) } : i).filter((i) => i.qty > 0));

  const mergeItems = (base, extra) => {
    const key = (i) => `${i.product_id || i.name}||${i.note || ""}`;
    const map = {}, order = [];
    [...base, ...extra].forEach((i) => {
      const k = key(i);
      if (!map[k]) { map[k] = { ...i }; order.push(k); } else { map[k] = { ...map[k], qty: map[k].qty + i.qty }; }
    });
    return order.map((k) => map[k]);
  };
  // In add-mode the cart holds ONLY the new additional items; totals still reflect the full bill.
  const effItems = addMode ? mergeItems(addBaseItems, cart) : cart;

  const subtotal = effItems.reduce((s, i) => s + i.price * i.qty, 0);
  const specialPct = (category) => {
    let best = 0;
    for (const r of specials) {
      if (r.scope === "category" && (r.category || "") !== (category || "")) continue;
      if ((r.percent || 0) > best) best = r.percent;
    }
    return best;
  };
  const specialDiscount = online ? effItems.reduce((s, i) => s + Math.round(i.price * i.qty * specialPct(i.category) / 100), 0) : 0;
  const afterSpecial = subtotal - specialDiscount;
  const discount = voucher ? (voucher.type === "nominal" ? Math.min(voucher.value, afterSpecial) : voucher.type === "percent" ? Math.round(afterSpecial * voucher.value / 100) : 0) : 0;
  const taxable = afterSpecial - discount;
  const taxRate = branch?.tax_pb1 ?? 10, svcRate = svcCharge;
  const service = Math.round(taxable * svcRate / 100);
  const tax = Math.round((taxable + service) * taxRate / 100);
  const total = taxable + service + tax;

  const openKasir = async () => {
    if (!openPwd.trim()) { toast.error("Masukkan password Anda untuk membuka kasir"); return; }
    try { const { data } = await api.post("/shift/open", { branch_id: branch.id, opening_cash: Number(openCash), password: openPwd }); setShift(data); setOpenPwd(""); toast.success(`Kasir dibuka · Modal awal ${fmtIDR(data.opening_cash)}`); }
    catch (e) { toast.error(apiError(e)); }
  };
  const closeKasir = async (password) => {
    try {
      const { data } = await api.post("/shift/close", { branch_id: branch.id, opening_cash: 0, password });
      setConfirmClose(false);
      setRecap({ ...data, autoPrinted: null }); setShift({});
      const opened = await printAny(recapHTML(data), { paper });
      setRecap((r) => r && { ...r, autoPrinted: opened });
    } catch (e) { toast.error(apiError(e)); }
  };

  const applyVoucher = async () => {
    try { const { data } = await api.get(`/vouchers/validate/${voucherCode.trim()}`); setVoucher(data); setShowVoucher(false); toast.success("Voucher diterapkan"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const buildLocalReceipt = (offlineId, createdAt) => {
    const sub = cart.reduce((s, i) => s + i.price * i.qty, 0);
    const sv = Math.round(sub * svcRate / 100);
    const tx = Math.round((sub + sv) * taxRate / 100);
    const tot = sub + sv + tx;
    return {
      receipt_no: "OFFLINE-" + offlineId.slice(0, 6).toUpperCase(),
      branch_name: branch.name, cashier_name: user.name, mode, table,
      items: cart.map((i) => ({ ...i })), subtotal: sub, discount: 0, tax: tx, service: sv, total: tot,
      tax_rate: taxRate, service_rate: svcRate,
      payment_method: pay.method, cash_received: pay.method === "cash" ? pay.cash : 0,
      change: pay.method === "cash" ? Math.max(0, pay.cash - tot) : 0,
      trace_number: pay.trace, voucher_code: "", created_at: createdAt, offline: true,
      receipt_footer: branch?.receipt_footer || "",
    };
  };

  const finishCheckout = (data, isOffline) => {
    setShowPay(false); setCart([]); setBillId(null); setTable(""); setVoucher(null); setVoucherCode(""); setPay({ method: "cash", cash: 0, trace: "" }); setAddMode(false); setAddBaseItems([]); setMember(null);
    setReceipt(data);
    printAny(receiptHTML(data), { paper });
    toast.success(isOffline ? "Transaksi OFFLINE tersimpan · disinkron saat online" : "Transaksi berhasil · struk dicetak");
    if (!isOffline && data?.member_name && data?.points_earned) toast.success(`⭐ ${data.member_name} +${data.points_earned} poin`);
    if (!isOffline) load();
  };

  const lookupMember = async () => {
    const p = memberInput.trim();
    if (!p) { toast.error("Masukkan No HP member"); return; }
    try { const { data } = await api.get("/members/lookup", { params: { phone: p } }); setMember(data); toast.success(`Member: ${data.name}`); setShowMember(false); setMemberInput(""); }
    catch (e) { toast.error(apiError(e)); }
  };

  const doCheckout = async () => {
    const offlineId = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(16).slice(2);
    const createdAt = new Date().toISOString();
    const payload = {
      branch_id: branch.id, mode, table, items: cart, payment_method: pay.method,
      cash_received: pay.method === "cash" ? pay.cash : 0, trace_number: pay.trace,
      voucher_code: online ? (voucher?.code || "") : "", send_to_kitchen: mode === "dinein",
      member_phone: member?.phone || "",
      bill_id: billId || "", offline_id: offlineId, created_at: createdAt,
    };
    if (online) {
      try {
        const { data } = await api.post("/checkout", payload);
        finishCheckout(data, false);
        return;
      } catch (e) {
        if (e?.response) { toast.error(apiError(e)); return; }
        // network error -> fall through to offline queue
        setOnline(false);
      }
    }
    const localReceipt = buildLocalReceipt(offlineId, createdAt);
    enqueue({ ...payload, offline: true });
    setPending(queueCount());
    finishCheckout(localReceipt, true);
  };

  const triggerLow = async (p) => {
    try { await api.post("/alerts", { product_id: p.id, product_name: p.name, branch_id: branch.id }); toast.success(`Notifikasi re-stock "${p.name}" dikirim ke Dapur`); }
    catch (e) { toast.error(apiError(e)); }
  };

  const savePaper = (v) => { setPaper(v); localStorage.setItem("jjl_paper", v); };
  const doTestPrint = () => { printAny(testHTML(), { paper }); toast.success("Test print dikirim"); };
  const doBluetooth = async () => { try { const n = await connectBluetooth(); setBtName(n); toast.success(`Terhubung: ${n}`); } catch (e) { toast.error(e.message); } };
  const saveServiceCharge = async () => {
    const pct = Number(svcInput);
    if (isNaN(pct) || pct < 0 || pct > 100) { toast.error("Service Charge harus 0-100%"); return; }
    if (!svcMgrPwd) { toast.error("Masukkan password Manager Cabang"); return; }
    setSavingSvc(true);
    try {
      const { data } = await api.put("/me/service-charge", { service_charge: pct, manager_password: svcMgrPwd });
      setSvcCharge(data.service_charge); setSvcInput(String(data.service_charge)); setSvcMgrPwd("");
      toast.success(`Service Charge kasir diset ${data.service_charge}%`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingSvc(false); }
  };

  const loadBills = async () => { try { const { data } = await api.get(`/bills?branch_id=${branch.id}`); setBills(data); } catch (e) { toast.error(apiError(e)); } };
  const openBillsDialog = async () => { await loadBills(); setShowBills(true); };
  const saveBill = async () => {
    if (cart.length === 0) return;
    try {
      await api.post("/bills", { id: billId, branch_id: branch.id, table, items: cart });
      toast.success(`Transaksi disimpan${table ? " · " + table : ""}`);
      setCart([]); setBillId(null); setTable(""); setVoucher(null); setVoucherCode("");
    } catch (e) { toast.error(apiError(e)); }
  };
  const openBill = (b) => { setCart(b.items || []); setTable(b.table || ""); setBillId(b.id); setMode("dinein"); setAddMode(false); setAddBaseItems([]); setShowBills(false); toast.success(`Bill ${b.table || ""} dibuka`); };
  const startAddMenu = (b) => {
    const base = (b.items || []).map((i) => ({ ...i }));
    setCart([]); setTable(b.table || ""); setBillId(b.id); setMode("dinein");
    setAddBaseItems(base); setAddMode(true); setShowBills(false);
    toast.success(`Tambah pesanan untuk ${b.table || "meja ini"} — pilih menu tambahan lalu "Tambah Pesanan"`);
  };
  const cancelAddMenu = () => {
    setAddMode(false); setAddBaseItems([]); setCart([]); setBillId(null); setTable("");
    toast("Tambah pesanan dibatalkan");
  };
  const calcTotals = (items) => {
    const sub = items.reduce((s, i) => s + i.price * i.qty, 0);
    const spDisc = online ? items.reduce((s, i) => s + Math.round(i.price * i.qty * specialPct(i.category) / 100), 0) : 0;
    const spLabels = online ? Array.from(new Set(specials.filter((r) => (r.percent || 0) > 0 && items.some((i) => r.scope !== "category" || (r.category || "") === (i.category || ""))).map((r) => r.name))) : [];
    const afterSp = sub - spDisc;
    const svcRate = svcCharge, taxRate = branch?.tax_pb1 ?? 10;
    const svc = Math.round(afterSp * svcRate / 100);
    const tx = Math.round((afterSp + svc) * taxRate / 100);
    return { subtotal: sub, special_discount: spDisc, special_label: spLabels.join(", "), service: svc, tax: tx, total: afterSp + svc + tx, service_rate: svcRate, tax_rate: taxRate };
  };
  const printBill = (items, tbl) => {
    if (!items || items.length === 0) { toast.error("Belum ada item"); return; }
    const t = { branch_name: branch?.name || "", table: tbl || "", items, ...calcTotals(items) };
    printAny(billHTML(t), { paper });
    toast.success("Bill dicetak");
  };
  const sendAdditional = async () => {
    const added = cart.filter((i) => i.qty > 0).map((i) => ({ ...i }));
    if (added.length === 0) { toast.error("Belum ada menu tambahan"); return; }
    setSendingAdd(true);
    try {
      const full = mergeItems(addBaseItems, added);
      await api.post(`/bills/${billId}/add-items`, { items: full, added });
      toast.success("Pesanan tambahan dikirim ke Dapur (KDS) & bill diperbarui");
      setAddMode(false); setAddBaseItems([]); setCart([]); setBillId(null); setTable("");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSendingAdd(false); }
  };
  const deleteBill = async (bid) => { try { await api.delete(`/bills/${bid}`); loadBills(); toast.success("Bill dihapus"); } catch (e) { toast.error(apiError(e)); } };
  const loadTxns = async () => { try { const { data } = await api.get(`/transactions?branch_id=${branch.id}`); setTxns(data); } catch (e) { toast.error(apiError(e)); } };
  const openTxnsDialog = async () => { await loadTxns(); setShowTxns(true); };
  const doCancel = async () => {
    try {
      await api.post(`/transactions/${cancelTarget.id}/cancel`, { password: cancelPwd });
      toast.success("Transaksi dibatalkan · stok & voucher dikembalikan");
      setCancelTarget(null); setCancelPwd(""); loadTxns(); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  // Shift gate
  if (shift && !shift.id) {
    return (
      <div className="flex min-h-screen flex-col bg-hotpot-grid">
        <TopBar />
        <div className="flex flex-1 items-center justify-center p-4">
          <Card className="card-surface w-full max-w-md rounded-3xl p-8 text-center red-glow" data-testid="open-kasir-panel">
            <DoorOpen className="mx-auto mb-3 text-crimson" size={44} />
            <h1 className="font-display text-2xl font-extrabold text-neutral-900">Buka Kasir</h1>
            <p className="mb-5 text-sm text-muted-foreground">Masukkan modal awal (uang laci) untuk memulai shift.</p>
            <Label className="text-left text-xs text-muted-foreground">Modal Awal</Label>
            <Input data-testid="open-cash-input" type="number" value={openCash} onChange={(e) => setOpenCash(e.target.value)}
              className="touch-btn mt-1 bg-neutral-100 border-neutral-200 text-center font-mono-num text-xl text-neutral-900" placeholder="0" />
            <div className="mt-3 flex flex-wrap justify-center gap-2">{[300000, 500000, 1000000].map((v) => <button key={v} onClick={() => setOpenCash(v)} className="rounded-lg bg-secondary px-3 py-1.5 text-xs font-bold text-neutral-900">{fmtIDR(v)}</button>)}</div>
            <Label className="mt-4 block text-left text-xs text-muted-foreground">Password Anda</Label>
            <Input data-testid="open-kasir-password" type="password" value={openPwd} onChange={(e) => setOpenPwd(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && openKasir()}
              className="touch-btn mt-1 bg-neutral-100 border-neutral-200 text-center text-neutral-900" placeholder="Masukkan password untuk konfirmasi" />
            <Button data-testid="open-kasir-btn" onClick={openKasir} className="mt-5 touch-btn w-full bg-crimson text-base font-bold hover:bg-red-500">Buka Kasir & Mulai</Button>
            <Button data-testid="open-kasir-attendance-btn" onClick={() => setShowAttendance(true)} variant="outline" className="mt-3 touch-btn w-full border-crimson/40 bg-crimson/5 font-bold text-crimson hover:bg-crimson/10"><Camera size={16} className="mr-2" /> Absensi Karyawan</Button>
          </Card>
        </div>
        <AttendanceDialog open={showAttendance} onOpenChange={setShowAttendance} />
        <RecapDialog recap={recap} onClose={() => setRecap(null)} paper={paper} />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-hotpot-grid">
      <TopBar>
        {!online && <span data-testid="offline-badge" className="flex items-center gap-1 rounded-full bg-amber-500/20 px-2.5 py-1 text-[11px] font-bold text-amber-400"><WifiOff size={13} /> Offline</span>}
        {pending > 0 && <Button data-testid="sync-btn" onClick={manualSync} variant="outline" size="sm" className="border-emerald-500/40 bg-emerald-500/10 text-xs text-emerald-400"><RefreshCw size={13} className="mr-1" /> Sinkron ({pending})</Button>}
        <Button data-testid="open-cfd-btn" onClick={() => window.open("/customer-display", "_blank")} variant="outline" size="sm" className="border-neutral-200 text-xs"><Monitor size={14} className="mr-1" /> Layar Pelanggan</Button>
        <Button data-testid="attendance-btn" onClick={() => setShowAttendance(true)} variant="outline" size="sm" className="border-crimson/40 bg-crimson/5 text-xs font-bold text-crimson hover:bg-crimson/10"><Camera size={14} className="mr-1" /> Absensi Karyawan</Button>
        <Button data-testid="txn-list-btn" onClick={openTxnsDialog} variant="outline" size="sm" className="border-neutral-200 text-xs"><ListOrdered size={14} className="mr-1" /> Daftar Transaksi</Button>
        <Button data-testid="settings-btn" onClick={() => setShowSettings(true)} variant="ghost" size="icon" className="text-muted-foreground hover:text-neutral-900"><Settings size={18} /></Button>
        <Button data-testid="close-kasir-btn" onClick={() => setConfirmClose(true)} variant="outline" size="sm" className="border-neutral-200 text-xs"><DoorClosed size={14} className="mr-1" /> Tutup Kasir</Button>
      </TopBar>

      <div data-testid="pos-action-toolbar" className="mx-auto flex w-full max-w-[1600px] flex-wrap items-center gap-2 px-4 pt-4">
        <div className="flex gap-1 rounded-xl bg-neutral-100 p-1">
          {[["self", "Self-Service"], ["dinein", "Dine-In"]].map(([k, l]) => (
            <button key={k} data-testid={`pos-mode-${k}`} onClick={() => setMode(k)} className={`touch-btn rounded-lg px-4 py-2 text-sm font-bold ${mode === k ? "bg-crimson text-white" : "text-muted-foreground"}`}>{l}</button>
          ))}
        </div>
        <Button data-testid="pos-member-btn" onClick={() => setShowMember(true)} variant="outline" className={`touch-btn font-bold ${member ? "border-yellow-400 bg-yellow-400/10 text-yellow-600" : "border-neutral-300 bg-neutral-100 text-neutral-900 hover:bg-neutral-200"}`}><UserRound size={16} className="mr-1" /> {member ? `${member.name} · ${member.points} poin` : "Member"}</Button>
        <Button data-testid="pos-redeem-voucher-btn" disabled={!online} onClick={() => setShowVoucher(true)} variant="outline" className="touch-btn border-gold/40 bg-gold/10 font-bold text-gold hover:bg-gold/20 disabled:opacity-40"><Ticket size={16} className="mr-1" /> Voucher</Button>
        <Button data-testid="pos-low-stock-alert-btn" disabled={!online} onClick={() => setShowLow(true)} variant="outline" className="touch-btn border-amber-500/40 bg-amber-500/10 font-bold text-amber-400 hover:bg-amber-500/20 disabled:opacity-40"><AlertTriangle size={16} className="mr-1" /> Low Stock</Button>
      </div>

      <div className="mx-auto grid w-full max-w-[1600px] flex-1 grid-cols-1 gap-4 px-4 pb-4 pt-3 lg:grid-cols-[400px_1fr]">
        <div className="lg:order-2">
          {mode === "dinein" && (
            <div className="mb-3 flex items-center gap-2">
              <LayoutGrid size={16} className="text-gold" />
              <Input data-testid="pos-table" value={table} onChange={(e) => setTable(e.target.value)} placeholder="No Meja (mis. T-05)" className="h-10 w-40 bg-neutral-100 border-neutral-200" />
            </div>
          )}
          <div className="mb-3 flex gap-2 overflow-x-auto pb-1">
            {cats.map((c) => (
              <button key={c} data-testid={`pos-cat-${c}`} onClick={() => setCat(c)} className={`shrink-0 rounded-full px-4 py-2 text-sm font-bold ${cat === c ? "bg-crimson text-white" : "card-surface text-muted-foreground"}`}>{c}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
            {shown.map((p, idx) => {
              const low = p.stock <= p.low_stock_threshold;
              return (
                <button key={p.id} data-testid={`pos-add-to-cart-item-${idx}`} onClick={() => add(p)}
                  className="card-surface group relative flex touch-btn flex-col items-start rounded-2xl p-3 text-left transition-all hover:border-crimson hover:red-glow active:scale-95">
                  {p.on_discount && <span className="absolute left-2 top-2 rounded-full bg-crimson px-1.5 py-0.5 text-[9px] font-bold text-white">DISKON</span>}
                  <div className="flex w-full items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start gap-1">
                        <span className="font-semibold leading-tight text-neutral-900">{p.name}</span>
                        {low && <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-500" />}
                      </div>
                      <span className="mt-0.5 block text-[10px] text-muted-foreground">Stok {p.stock}</span>
                      <div className="mt-1 flex items-baseline gap-1.5">
                        <span className="font-mono-num text-sm font-bold text-gold">{fmtIDR(priceOf(p))}</span>
                        {p.on_discount && <span className="font-mono-num text-[10px] text-muted-foreground line-through">{fmtIDR(p.price)}</span>}
                      </div>
                    </div>
                    {p.image_id && <img src={`${process.env.REACT_APP_BACKEND_URL}/api/products/photo/${p.image_id}`} alt="" loading="lazy" className="h-12 w-12 shrink-0 rounded-md object-cover" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <Card className="card-surface flex h-[calc(100vh-140px)] flex-col rounded-2xl p-4 lg:order-1 lg:sticky lg:top-20">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2 font-display font-bold text-neutral-900"><ShoppingCart size={18} className="text-crimson" /> {addMode ? "Pesanan Tambahan" : "Pesanan"} {mode === "dinein" && table && <span className="text-gold">· {table}</span>}</div>
            {cart.length > 0 && <button data-testid="pos-clear-cart" onClick={() => setCart([])} className="text-xs text-muted-foreground hover:text-crimson">Kosongkan</button>}
          </div>

          <div className="flex-1 space-y-2 overflow-y-auto">
            {addMode && (
              <div data-testid="add-mode-banner" className="mb-1 rounded-xl border border-amber-400/50 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700">
                {addBaseItems.reduce((s, i) => s + i.qty, 0)} item sudah dipesan sebelumnya (disembunyikan). Hanya menu tambahan baru yang tampil di sini.
              </div>
            )}
            {cart.length === 0 && <div className="flex h-full flex-col items-center justify-center text-center text-muted-foreground"><ShoppingCart size={36} className="mb-2 opacity-40" /><p className="text-sm">{addMode ? <>Belum ada tambahan.<br />Ketuk menu untuk menambah pesanan.</> : <>Keranjang kosong.<br />Ketuk menu untuk menambah.</>}</p></div>}
            {cart.map((i) => (
              <div key={i.product_id} data-testid={`cart-item-${i.product_id}`} className="flex items-center gap-2 rounded-xl bg-neutral-100 p-2">
                <div className="flex-1"><div className="text-sm font-semibold text-neutral-900">{i.name}</div>
                  <div className="flex items-center gap-1.5">
                    {i.on_discount && i.orig_price > i.price && <span className="font-mono-num text-[11px] text-muted-foreground line-through">{fmtIDR(i.orig_price)}</span>}
                    <span className="font-mono-num text-xs text-gold">{fmtIDR(i.price)}</span>
                  </div>
                </div>
                <button data-testid={`qty-minus-${i.product_id}`} onClick={() => changeQty(i.product_id, -1)} className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary text-neutral-900"><Minus size={14} /></button>
                <span className="w-6 text-center font-mono-num font-bold text-neutral-900">{i.qty}</span>
                <button data-testid={`qty-plus-${i.product_id}`} onClick={() => changeQty(i.product_id, 1)} className="flex h-8 w-8 items-center justify-center rounded-lg bg-crimson text-white"><Plus size={14} /></button>
              </div>
            ))}
          </div>

          {voucher && (
            <div className="mt-2 flex items-center justify-between rounded-xl border border-gold/40 bg-gold/10 px-3 py-2">
              <span className="text-xs font-semibold text-gold">Voucher {voucher.code} diterapkan</span>
              <button onClick={() => { setVoucher(null); setVoucherCode(""); }} className="text-gold"><X size={14} /></button>
            </div>
          )}

          <div className="mt-3 space-y-1 border-t border-neutral-200 pt-3 text-sm">
            <Row label="Subtotal" value={fmtIDR(subtotal)} />
            {specialDiscount > 0 && <Row label="Diskon Khusus" value={`- ${fmtIDR(specialDiscount)}`} accent="text-emerald-400" />}
            {discount > 0 && <Row label="Diskon Voucher" value={`- ${fmtIDR(discount)}`} accent="text-emerald-400" />}
            <Row label={`Service (${svcRate}%)`} value={fmtIDR(service)} muted />
            <Row label={`PB1 (${taxRate}%)`} value={fmtIDR(tax)} muted />
            <div className="flex items-center justify-between pt-1"><span className="font-display font-bold text-neutral-900">TOTAL</span><span data-testid="pos-total" className="font-mono-num text-2xl font-extrabold text-gold">{fmtIDR(total)}</span></div>
          </div>

          {mode === "dinein" && !addMode && (
            <>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Button data-testid="save-bill-btn" onClick={saveBill} disabled={cart.length === 0} variant="outline" className="touch-btn border-emerald-500/40 bg-emerald-500/10 font-bold text-emerald-400 hover:bg-emerald-500/20"><Save size={16} className="mr-1" /> {billId ? "Update Simpan" : "Simpan"}</Button>
                <Button data-testid="saved-bills-btn" onClick={openBillsDialog} variant="outline" className="touch-btn border-sky-500/40 bg-sky-500/10 font-bold text-sky-400 hover:bg-sky-500/20"><FileText size={16} className="mr-1" /> Tersimpan</Button>
              </div>
              <div className="mt-2 grid grid-cols-1 gap-2">
                <Button data-testid="print-bill-btn" onClick={() => printBill(cart, table)} disabled={cart.length === 0} variant="outline" className="touch-btn border-neutral-300 bg-neutral-100 font-bold text-neutral-900 hover:bg-neutral-200"><Printer size={16} className="mr-1" /> Cetak Bill (Sebelum Bayar)</Button>
              </div>
            </>
          )}
          {mode === "dinein" && addMode && (
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Button data-testid="send-additional-btn" onClick={sendAdditional} disabled={cart.length === 0 || sendingAdd} className="touch-btn bg-amber-500 font-bold text-black hover:bg-amber-400"><ChefHat size={16} className="mr-1" /> Tambah Pesanan</Button>
              <Button data-testid="cancel-add-btn" onClick={cancelAddMenu} disabled={sendingAdd} variant="outline" className="touch-btn border-neutral-300 bg-neutral-100 font-bold text-neutral-900 hover:bg-neutral-200"><X size={16} className="mr-1" /> Batal</Button>
            </div>
          )}
          {!addMode && (
            <Button data-testid="pos-checkout-button" disabled={cart.length === 0} onClick={() => { setPay({ ...pay, cash: total }); setShowPay(true); }}
              className="mt-2 touch-btn w-full bg-crimson text-base font-bold hover:bg-red-500"><Wallet size={18} className="mr-1" /> Bayar {fmtIDR(total)}</Button>
          )}
        </Card>
      </div>

      <AttendanceDialog open={showAttendance} onOpenChange={setShowAttendance} />

      <Dialog open={showMember} onOpenChange={setShowMember}>
        <DialogContent className="glass border-neutral-200 text-neutral-900">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><UserRound size={18} className="text-crimson" /> Member</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Masukkan No HP member atau scan kode batang member. Poin bertambah otomatis setelah pembayaran.</p>
          <Input data-testid="member-input" autoFocus value={memberInput} onChange={(e) => setMemberInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") lookupMember(); }} placeholder="No HP / scan barcode member" className="touch-btn bg-neutral-100 border-neutral-200 font-mono-num" />
          <div className="grid grid-cols-2 gap-2">
            <Button data-testid="member-lookup-btn" onClick={lookupMember} className="touch-btn bg-crimson font-bold hover:bg-red-500">Cari Member</Button>
            {member && <Button data-testid="member-clear-btn" onClick={() => { setMember(null); toast.success("Member dilepas"); }} variant="outline" className="touch-btn border-neutral-300 font-bold text-neutral-900">Lepas Member</Button>}
          </div>
          {member && <div data-testid="member-attached" className="rounded-xl bg-yellow-400/10 p-3 text-sm text-neutral-900"><b>{member.name}</b> · {member.phone} · <span className="font-bold text-gold">{member.points} poin</span></div>}
        </DialogContent>
      </Dialog>

      <Dialog open={showPay} onOpenChange={setShowPay}>
        <DialogContent className="glass border-neutral-200 text-neutral-900">
          <DialogHeader><DialogTitle className="font-display">Pembayaran · {fmtIDR(total)}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-4 gap-2">
            {PAY.map(([k, l]) => (
              <button key={k} data-testid={`pay-method-${k}`} onClick={() => setPay({ ...pay, method: k })} className={`touch-btn rounded-xl text-sm font-bold ${pay.method === k ? "bg-crimson text-white" : "card-surface text-muted-foreground"}`}>{l}</button>
            ))}
          </div>
          {pay.method === "cash" && (
            <div>
              <Label className="text-xs text-muted-foreground">Uang Diterima</Label>
              <Input data-testid="pay-cash" type="number" value={pay.cash} onChange={(e) => setPay({ ...pay, cash: Number(e.target.value) })} className="touch-btn mt-1 bg-neutral-100 border-neutral-200 font-mono-num text-lg" />
              <div className="mt-2 flex flex-wrap gap-2">{QUICK_CASH.map((v) => <button key={v} onClick={() => setPay({ ...pay, cash: v })} className="rounded-lg bg-secondary px-3 py-1.5 text-xs font-bold text-neutral-900">{fmtIDR(v)}</button>)}</div>
              <div className="mt-2 flex justify-between rounded-lg bg-neutral-100 px-3 py-2"><span className="text-sm text-muted-foreground">Kembalian</span><span data-testid="pay-change" className="font-mono-num font-bold text-emerald-400">{fmtIDR(Math.max(0, pay.cash - total))}</span></div>
            </div>
          )}
          {pay.method === "qris" && (
            <div className="flex flex-col items-center rounded-xl bg-white p-4">
              <img alt="QRIS" src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=JJL-QRIS-${total}`} />
              <span className="mt-2 font-mono-num font-bold text-ebony">{fmtIDR(total)}</span>
            </div>
          )}
          {(pay.method === "debit" || pay.method === "credit") && (
            <div><Label className="text-xs text-muted-foreground">Trace Number (EDC)</Label><Input data-testid="pay-trace" value={pay.trace} onChange={(e) => setPay({ ...pay, trace: e.target.value })} className="touch-btn mt-1 bg-neutral-100 border-neutral-200 font-mono-num" placeholder="000123456" /></div>
          )}
          <Button data-testid="pay-confirm-btn" disabled={pay.method === "cash" && pay.cash < total} onClick={doCheckout} className="touch-btn w-full bg-crimson text-base font-bold hover:bg-red-500"><CheckCircle2 size={18} className="mr-1" /> Konfirmasi & Cetak Struk</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={showVoucher} onOpenChange={setShowVoucher}>
        <DialogContent className="glass border-neutral-200 text-neutral-900">
          <DialogHeader><DialogTitle className="font-display">Redeem Voucher Member</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Scan QR atau input kode unik voucher member.</p>
          <Input data-testid="voucher-code-input" value={voucherCode} onChange={(e) => setVoucherCode(e.target.value.toUpperCase())} placeholder="JJL-XXXXXXXX" className="touch-btn bg-neutral-100 border-neutral-200 font-mono-num" />
          <Button data-testid="voucher-apply-btn" onClick={applyVoucher} className="touch-btn w-full bg-crimson font-bold hover:bg-red-500">Terapkan Voucher</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={showLow} onOpenChange={setShowLow}>
        <DialogContent className="glass border-neutral-200 text-neutral-900 max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><AlertTriangle className="text-amber-400" size={18} /> Pilih Menu Habis</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Ketuk menu yang habis untuk kirim notifikasi re-stock ke Admin Dapur.</p>
          <div className="grid grid-cols-2 gap-2">
            {products.map((p) => (
              <button key={p.id} data-testid={`low-stock-item-${p.id}`} onClick={() => triggerLow(p)} className="card-surface rounded-xl p-3 text-left text-sm font-semibold text-neutral-900 hover:border-amber-500">{p.name}<div className="text-[10px] text-muted-foreground">Stok {p.stock}</div></button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!receipt} onOpenChange={(o) => !o && setReceipt(null)}>
        <DialogContent className="border-neutral-200 bg-white text-ebony">
          <DialogHeader><DialogTitle className="font-display text-ebony">Struk Transaksi</DialogTitle></DialogHeader>
          {receipt && <ReceiptView t={receipt} />}
          <Button data-testid="receipt-print-button" onClick={() => printAny(receiptHTML(receipt), { paper })} className="w-full bg-crimson font-bold text-white hover:bg-red-500"><Printer size={16} className="mr-1" /> Cetak Ulang</Button>
        </DialogContent>
      </Dialog>

      {/* Settings */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="glass border-neutral-200 text-neutral-900 max-h-[90vh] overflow-y-auto" data-testid="settings-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Settings size={18} className="text-crimson" /> Pengaturan Kasir</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            <Button data-testid="bluetooth-btn" onClick={doBluetooth} variant="outline" className="touch-btn border-cyan-500/40 bg-cyan-500/10 font-bold text-cyan-600"><Bluetooth size={16} className="mr-1" /> {btName || (bluetoothConnected() ? "Terhubung" : "Hubungkan Bluetooth")}</Button>
            <Button data-testid="test-print-btn" onClick={doTestPrint} className="touch-btn bg-crimson font-bold hover:bg-red-500"><Printer size={16} className="mr-1" /> Test Print</Button>
          </div>
          <div className="rounded-xl bg-neutral-100 p-3 space-y-2" data-testid="svc-charge-section">
            <div className="text-sm font-semibold text-neutral-900">Service Charge Kasir <span className="font-mono-num text-gold">({svcCharge}%)</span></div>
            <div className="text-[11px] text-muted-foreground">Atur % Service Charge untuk kasir ini. Perubahan perlu otorisasi password Manager Cabang.</div>
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="svc-charge-input" type="number" value={svcInput} onChange={(e) => setSvcInput(e.target.value)} placeholder="Service Charge %" className="touch-btn bg-neutral-100 border-neutral-200 font-mono-num" />
              <Input data-testid="svc-mgr-pwd-input" type="password" value={svcMgrPwd} onChange={(e) => setSvcMgrPwd(e.target.value)} placeholder="Password Manager" className="touch-btn bg-neutral-100 border-neutral-200" />
            </div>
            <Button data-testid="save-svc-charge-btn" onClick={saveServiceCharge} disabled={savingSvc} className="w-full touch-btn bg-crimson font-bold hover:bg-red-500"><LockKeyhole size={15} className="mr-1" /> Simpan Service Charge</Button>
          </div>
          <div className="mt-2 border-t border-neutral-200 pt-3">
            <Label className="text-xs text-muted-foreground">Ukuran Kertas Struk</Label>
            <div className="mt-1 flex gap-2">
              {["58", "80"].map((s) => <button key={s} data-testid={`paper-${s}`} onClick={() => savePaper(s)} className={`touch-btn flex-1 rounded-xl text-sm font-bold ${paper === s ? "bg-crimson text-white" : "card-surface text-muted-foreground"}`}>{s}mm</button>)}
            </div>
          </div>
          <div className="flex items-center justify-between rounded-xl bg-neutral-100 px-3 py-2">
            <div><div className="text-sm font-semibold text-neutral-900">Auto Cut Struk</div><div className="text-[11px] text-muted-foreground">Potong kertas otomatis (jika printer mendukung)</div></div>
            <Switch data-testid="autocut-toggle" checked={autoCut} onCheckedChange={(v) => { setAutoCut(v); localStorage.setItem("jjl_autocut", v ? "1" : "0"); }} />
          </div>
          <BtModeSetting />
          <p className="text-xs text-muted-foreground">Cetak menggunakan dialog printer sistem — mendukung semua printer di PC/HP (USB, jaringan, atau Bluetooth). Opsional: hubungkan printer thermal Bluetooth langsung dengan tombol di atas.</p>
        </DialogContent>
      </Dialog>

      <Dialog open={showBills} onOpenChange={setShowBills}>
        <DialogContent className="glass border-neutral-200 text-neutral-900 max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><FileText size={18} className="text-sky-400" /> Transaksi Tersimpan</DialogTitle></DialogHeader>
          {bills.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Belum ada transaksi tersimpan.</p>}
          <div className="space-y-2">
            {bills.map((b) => {
              const tot = (b.items || []).reduce((s, i) => s + i.price * i.qty, 0);
              return (
                <div key={b.id} data-testid={`saved-bill-${b.id}`} className="flex flex-wrap items-center gap-2 rounded-xl bg-neutral-100 p-3">
                  <div className="min-w-[120px] flex-1">
                    <div className="font-bold text-neutral-900">{b.table || "Tanpa Meja"} · <span className="font-mono-num text-gold">{fmtIDR(tot)}</span></div>
                    <div className="text-[11px] text-muted-foreground">{(b.items || []).length} item · {new Date(b.created_at).toLocaleString("id-ID", { hour: "2-digit", minute: "2-digit" })}</div>
                  </div>
                  <Button data-testid={`open-bill-${b.id}`} onClick={() => openBill(b)} size="sm" className="bg-crimson font-bold hover:bg-red-500">Buka</Button>
                  <Button data-testid={`add-menu-bill-${b.id}`} onClick={() => startAddMenu(b)} size="sm" className="bg-amber-500 font-bold text-black hover:bg-amber-400"><Plus size={14} className="mr-1" /> Tambah Menu</Button>
                  <Button data-testid={`print-bill-${b.id}`} onClick={() => printBill(b.items, b.table)} size="sm" variant="outline" className="border-neutral-300 font-bold text-neutral-900 hover:bg-neutral-200"><Printer size={14} className="mr-1" /> Cetak Bill</Button>
                  <button data-testid={`delete-bill-${b.id}`} onClick={() => deleteBill(b.id)} className="text-muted-foreground hover:text-crimson"><X size={16} /></button>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showTxns} onOpenChange={setShowTxns}>
        <DialogContent className="glass border-neutral-200 text-neutral-900 max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><ListOrdered size={18} className="text-crimson" /> Daftar Transaksi</DialogTitle></DialogHeader>
          {txns.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Belum ada transaksi.</p>}
          <div className="space-y-2">
            {txns.map((t) => (
              <div key={t.id} data-testid={`txn-row-${t.id}`} className="flex items-center gap-2 rounded-xl bg-neutral-100 p-3">
                <div className="flex-1">
                  <div className="font-mono-num font-bold text-neutral-900">{t.receipt_no}</div>
                  <div className="text-[11px] text-muted-foreground">{new Date(t.created_at).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })} · {(t.payment_method || "").toUpperCase()}{t.status === "cancelled" ? " · DIBATALKAN" : ""}</div>
                </div>
                <span className={`font-mono-num font-bold ${t.status === "cancelled" ? "text-muted-foreground line-through" : "text-gold"}`}>{fmtIDR(t.total)}</span>
                <Button data-testid={`open-txn-${t.id}`} onClick={() => { setReceipt(t); setShowTxns(false); }} size="sm" variant="outline" className="border-neutral-300 bg-white font-bold text-neutral-900 hover:bg-neutral-200"><Eye size={14} className="mr-1" /> Buka</Button>
                {t.status !== "cancelled" && <Button data-testid={`cancel-txn-${t.id}`} onClick={() => { setCancelTarget(t); setCancelPwd(""); }} size="sm" variant="outline" className="border-crimson/40 bg-crimson/10 font-bold text-crimson hover:bg-crimson/20"><Ban size={14} className="mr-1" /> Batalkan</Button>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <DialogContent className="glass border-neutral-200 text-neutral-900">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Ban size={18} className="text-crimson" /> Batalkan Transaksi</DialogTitle></DialogHeader>
          {cancelTarget && <p className="text-sm text-muted-foreground">Batalkan <b className="font-mono-num text-neutral-900">{cancelTarget.receipt_no}</b> ({fmtIDR(cancelTarget.total)})? Stok & voucher akan dikembalikan. Masukkan <b className="text-gold">password Manager Cabang</b> untuk konfirmasi.</p>}
          <Input data-testid="cancel-password-input" type="password" value={cancelPwd} onChange={(e) => setCancelPwd(e.target.value)} placeholder="Password Manager Cabang" className="touch-btn bg-neutral-100 border-neutral-200" />
          <Button data-testid="cancel-confirm-btn" disabled={!cancelPwd} onClick={doCancel} className="touch-btn w-full bg-crimson font-bold hover:bg-red-500">Konfirmasi Pembatalan</Button>
        </DialogContent>
      </Dialog>

      <ConfirmCloseDialog open={confirmClose} onYes={closeKasir} onNo={() => setConfirmClose(false)} />
      <RecapDialog recap={recap} onClose={() => setRecap(null)} paper={paper} />
    </div>
  );
}

function BtModeSetting() {
  const [mode, setMode] = useState(localStorage.getItem("jjl_bt_mode") || "image");
  const pick = (m) => { setMode(m); localStorage.setItem("jjl_bt_mode", m); };
  return (
    <div className="rounded-xl bg-neutral-100 px-3 py-2">
      <div className="text-sm font-semibold text-neutral-900">Mode Cetak Bluetooth</div>
      <div className="mb-2 text-[11px] text-muted-foreground">Pilih "Teks" jika printer mencetak kode/karakter aneh.</div>
      <div className="flex gap-2">
        {[["image", "Gambar (sama dengan PC)"], ["text", "Teks (semua printer)"]].map(([k, l]) => (
          <button key={k} data-testid={`bt-mode-${k}`} onClick={() => pick(k)}
            className={`touch-btn flex-1 rounded-xl text-xs font-bold ${mode === k ? "bg-crimson text-white" : "card-surface text-muted-foreground"}`}>{l}</button>
        ))}
      </div>
    </div>
  );
}

function ConfirmCloseDialog({ open, onYes, onNo }) {
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) { setPwd(""); } }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    if (!pwd) { toast.error("Masukkan password Anda untuk menutup kasir"); return; }
    setBusy(true); await onYes(pwd); setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onNo()}>
      <DialogContent className="glass border-neutral-200 text-neutral-900" data-testid="confirm-close-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><DoorClosed size={18} className="text-crimson" /> Tutup Kasir?</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Shift akan ditutup dan laporan tutup kasir langsung dicetak. Masukkan password Anda untuk konfirmasi.</p>
        <form onSubmit={submit} className="space-y-3">
          <Input data-testid="confirm-close-password" type="password" autoFocus autoComplete="current-password" value={pwd}
            onChange={(e) => setPwd(e.target.value)} placeholder="Password kasir" className="touch-btn bg-neutral-100 border-neutral-200" />
          <div className="grid grid-cols-2 gap-3">
            <Button type="button" data-testid="confirm-close-no" variant="outline" onClick={onNo} className="touch-btn font-bold">Tidak</Button>
            <Button type="submit" data-testid="confirm-close-yes" disabled={busy} className="touch-btn bg-crimson font-bold hover:bg-red-500">{busy ? <Loader2 className="animate-spin" /> : "Iya"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function recapHTML(recap) {
  const txns = (recap.transactions || []).filter((t) => t.status !== "cancelled");
  const txnLines = txns.map((t) => `<div class="r"><span>${t.receipt_no}</span><span>${fmtIDR(t.total)}</span></div>`).join("");
  return `<div class="c b lg">TUTUP KASIR</div><div class="c">${recap.cashier_name}</div><div class="dash"></div>
    <div class="r"><span>Modal Awal</span><span>${fmtIDR(recap.opening_cash)}</span></div>
    <div class="r"><span>Penjualan Tunai</span><span>${fmtIDR(recap.cash_sales)}</span></div>
    <div class="r"><span>Non-Tunai</span><span>${fmtIDR(recap.non_cash_sales)}</span></div>
    <div class="r b"><span>Total Penjualan</span><span>${fmtIDR(recap.total_sales)}</span></div>
    <div class="r"><span>Jumlah Transaksi</span><span>${recap.transaction_count}</span></div>
    <div class="dash"></div><div class="r b"><span>Kas Seharusnya</span><span>${fmtIDR(recap.expected_cash)}</span></div>
    ${txns.length ? `<div class="dash"></div><div class="c b">DAFTAR TRANSAKSI</div>${txnLines}` : ""}`;
}

function RecapDialog({ recap, onClose, paper }) {
  if (!recap) return null;
  const txns = (recap.transactions || []).filter((t) => t.status !== "cancelled");
  const printRecap = () => printAny(recapHTML(recap), { paper });
  return (
    <Dialog open={!!recap} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass border-neutral-200 text-neutral-900 max-h-[85vh] overflow-y-auto" data-testid="close-recap">
        <DialogHeader><DialogTitle className="font-display">Rekap Penjualan Shift</DialogTitle></DialogHeader>
        <div className="space-y-1 rounded-xl bg-neutral-100 p-4 text-sm">
          <Row label="Modal Awal" value={fmtIDR(recap.opening_cash)} />
          <Row label="Penjualan Tunai" value={fmtIDR(recap.cash_sales)} />
          <Row label="Non-Tunai" value={fmtIDR(recap.non_cash_sales)} muted />
          <div className="flex justify-between pt-1"><span className="font-bold text-neutral-900">Total Penjualan</span><span className="font-mono-num font-bold text-gold">{fmtIDR(recap.total_sales)}</span></div>
          <Row label="Jumlah Transaksi" value={recap.transaction_count} muted />
          <div className="flex justify-between border-t border-neutral-200 pt-2"><span className="font-bold text-neutral-900">Kas Seharusnya</span><span className="font-mono-num font-bold text-emerald-400">{fmtIDR(recap.expected_cash)}</span></div>
        </div>
        {txns.length > 0 && (
          <div className="rounded-xl bg-neutral-100 p-4" data-testid="recap-txn-list">
            <div className="mb-2 text-xs font-bold uppercase text-muted-foreground">Daftar Transaksi</div>
            <div className="max-h-48 space-y-1 overflow-y-auto">
              {txns.map((t) => (
                <div key={t.id} className="flex justify-between text-sm">
                  <span className="font-mono-num text-neutral-900">{t.receipt_no}</span>
                  <span className="font-mono-num text-gold">{fmtIDR(t.total)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {recap.autoPrinted === false && (
          <p data-testid="recap-autoprint-failed" className="rounded-xl bg-amber-50 px-3 py-2 text-center text-sm font-semibold text-amber-700">Layar cetak tidak terbuka otomatis. Tekan tombol di bawah.</p>
        )}
        <Button data-testid="print-recap-btn" onClick={printRecap} className="h-16 w-full bg-crimson text-lg font-extrabold hover:bg-red-500"><Printer size={22} className="mr-2" /> Cetak Laporan</Button>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, muted, accent }) {
  return <div className="flex justify-between"><span className={muted ? "text-muted-foreground" : "text-neutral-900"}>{label}</span><span className={`font-mono-num ${accent || (muted ? "text-muted-foreground" : "text-neutral-900")}`}>{value}</span></div>;
}

function ReceiptView({ t }) {
  return (
    <div data-testid="receipt-view" className="font-mono-num text-xs leading-relaxed text-ebony">
      <div className="text-center">
        <div className="font-display text-base font-extrabold">DA LE TANG HuoGuo</div>
        <div>{t.branch_name}</div>
        <div className="my-1 border-t border-dashed border-black/40" />
        <div className="flex justify-between"><span>No: {t.receipt_no}</span><span>{t.mode === "dinein" ? `Meja ${t.table}` : "Self"}</span></div>
        <div className="flex justify-between"><span>Kasir: {t.cashier_name}</span></div>
      </div>
      <div className="my-1 border-t border-dashed border-black/40" />
      {t.items.map((i, k) => (<div key={k} className="flex justify-between"><span>{i.qty}x {i.name}</span><span>{fmtIDR(i.price * i.qty)}</span></div>))}
      <div className="my-1 border-t border-dashed border-black/40" />
      <div className="flex justify-between"><span>Subtotal</span><span>{fmtIDR(t.subtotal)}</span></div>
      {t.special_discount > 0 && <div className="flex justify-between"><span>Diskon Khusus{t.special_label ? ` (${t.special_label})` : ""}</span><span>-{fmtIDR(t.special_discount)}</span></div>}
      {t.discount > 0 && <div className="flex justify-between"><span>Diskon</span><span>-{fmtIDR(t.discount)}</span></div>}
      <div className="flex justify-between"><span>Service {t.service_rate}%</span><span>{fmtIDR(t.service)}</span></div>
      <div className="flex justify-between"><span>PB1 {t.tax_rate}%</span><span>{fmtIDR(t.tax)}</span></div>
      <div className="my-1 border-t border-dashed border-black/40" />
      <div className="flex justify-between text-sm font-bold"><span>TOTAL</span><span>{fmtIDR(t.total)}</span></div>
      <div className="flex justify-between"><span>{t.payment_method.toUpperCase()}</span><span>{t.payment_method === "cash" ? fmtIDR(t.cash_received) : t.trace_number || "-"}</span></div>
      {t.payment_method === "cash" && <div className="flex justify-between"><span>Kembali</span><span>{fmtIDR(t.change)}</span></div>}
      <div className="my-1 border-t border-dashed border-black/40" />
      <div className="text-center">Terima kasih & selamat menikmati! 🔥</div>
    </div>
  );
}
